import React, { useState } from 'react';
import { 
  FunnelIcon,
  XMarkIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const AdvancedLeadFilters = ({ onFilterChange, activeFilters, leads }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    source: [],
    assignedTo: [],
    dateRange: { start: '', end: '' },
    valueRange: { min: '', max: '' },
    priority: []
  });

  const leadSources = [
    { value: 'website', label: '🌐 Website', count: 0 },
    { value: 'instagram', label: '📷 Instagram', count: 0 },
    { value: 'facebook', label: '📘 Facebook', count: 0 },
    { value: 'whatsapp', label: '💬 WhatsApp', count: 0 },
    { value: 'linkedin', label: '💼 LinkedIn', count: 0 },
    { value: 'referral', label: '👥 Referral', count: 0 },
    { value: 'email_campaign', label: '📧 Email Campaign', count: 0 },
    { value: 'phone_call', label: '📞 Phone Call', count: 0 }
  ];

  const leadStatuses = [
    { value: 'new', label: '🆕 New', color: 'yellow' },
    { value: 'contacted', label: '📞 Contacted', color: 'blue' },
    { value: 'qualified', label: '✅ Qualified', color: 'green' },
    { value: 'proposal', label: '📋 Proposal', color: 'purple' },
    { value: 'negotiation', label: '🤝 Negotiation', color: 'orange' },
    { value: 'closed_won', label: '🎉 Won', color: 'green' },
    { value: 'closed_lost', label: '❌ Lost', color: 'red' },
    { value: 'on_hold', label: '⏸️ On Hold', color: 'gray' }
  ];

  const priorities = [
    { value: 'low', label: '🔵 Low', color: 'blue' },
    { value: 'medium', label: '🟡 Medium', color: 'yellow' },
    { value: 'high', label: '🟠 High', color: 'orange' },
    { value: 'urgent', label: '🔴 Urgent', color: 'red' }
  ];

  const handleFilterChange = (filterType, value, checked) => {
    const newFilters = { ...filters };
    
    if (checked) {
      newFilters[filterType] = [...newFilters[filterType], value];
    } else {
      newFilters[filterType] = newFilters[filterType].filter(item => item !== value);
    }
    
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    const emptyFilters = {
      status: [],
      source: [],
      assignedTo: [],
      dateRange: { start: '', end: '' },
      valueRange: { min: '', max: '' },
      priority: []
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const getActiveFilterCount = () => {
    return filters.status.length + 
           filters.source.length + 
           filters.assignedTo.length + 
           filters.priority.length +
           (filters.dateRange.start || filters.dateRange.end ? 1 : 0) +
           (filters.valueRange.min || filters.valueRange.max ? 1 : 0);
  };

  return (
    <div className="relative">
      {/* Filter Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center px-4 py-2 rounded-lg border transition-all duration-200 ${
          getActiveFilterCount() > 0 
            ? 'bg-blue-50 border-blue-300 text-blue-700' 
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <FunnelIcon className="h-5 w-5 mr-2" />
        <span className="font-medium">Filters</span>
        {getActiveFilterCount() > 0 && (
          <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-2 py-1">
            {getActiveFilterCount()}
          </span>
        )}
      </button>

      {/* Filter Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Status
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {leadStatuses.map((status) => (
                    <label key={status.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.status.includes(status.value)}
                        onChange={(e) => handleFilterChange('status', status.value, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{status.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Source Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Source
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {leadSources.map((source) => (
                    <label key={source.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.source.includes(source.value)}
                        onChange={(e) => handleFilterChange('source', source.value, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{source.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority Level
                </label>
                <div className="space-y-2">
                  {priorities.map((priority) => (
                    <label key={priority.value} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.priority.includes(priority.value)}
                        onChange={(e) => handleFilterChange('priority', priority.value, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{priority.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Date Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Start date"
                  />
                  <input
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, end: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="End date"
                  />
                </div>
              </div>

              {/* Value Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CurrencyDollarIcon className="h-4 w-4 inline mr-1" />
                  Lead Value Range (₹)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    value={filters.valueRange.min}
                    onChange={(e) => setFilters({
                      ...filters,
                      valueRange: { ...filters.valueRange, min: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Min value"
                  />
                  <input
                    type="number"
                    value={filters.valueRange.max}
                    onChange={(e) => setFilters({
                      ...filters,
                      valueRange: { ...filters.valueRange, max: e.target.value }
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Max value"
                  />
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  onFilterChange(filters);
                  setIsOpen(false);
                }}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedLeadFilters;
